import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Calendar, MapPin, DollarSign, Plus, Settings, Search, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import { TripEditModal } from './TripEditModal';

interface Trip {
  id: string;
  title: string;
  destination: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  trip_type: string;
  budget: number | null;
  currency: string;
  is_active: boolean;
}

interface TripsListProps {
  onCreateTrip: () => void;
  onSelectTrip: (trip: Trip) => void;
  refreshTrigger?: number;
}

export function TripsList({ onCreateTrip, onSelectTrip, refreshTrigger }: TripsListProps) {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { user } = useAuth();

  const loadTrips = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('trips')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTrips(data || []);
    } catch (error) {
      console.error('Error loading trips:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrips();
  }, [user, refreshTrigger]);

  const filteredTrips = trips.filter(trip => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      trip.title.toLowerCase().includes(query) ||
      trip.destination.toLowerCase().includes(query) ||
      trip.destination_country.toLowerCase().includes(query)
    );
  });

  const handleEditTrip = (trip: Trip, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTrip(trip);
    setEditModalOpen(true);
  };

  const handleTripUpdated = () => {
    loadTrips();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Your Trips</h2>
          <p className="text-muted-foreground">Manage your travel plans</p>
        </div>
        <Button onClick={onCreateTrip} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Trip
        </Button>
      </div>

      {trips.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search trips by name, destination, or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {trips.length === 0 ? (
        <Card className="text-center p-8">
          <CardContent className="pt-6">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No trips yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first trip to get started with planning your adventure
            </p>
            <Button onClick={onCreateTrip}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Trip
            </Button>
          </CardContent>
        </Card>
      ) : filteredTrips.length === 0 ? (
        <Card className="text-center p-8">
          <CardContent className="pt-6">
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No trips found</h3>
            <p className="text-muted-foreground mb-4">
              No trips match your search "{searchQuery}"
            </p>
            <Button variant="outline" onClick={() => setSearchQuery('')}>
              Clear Search
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrips.map((trip) => (
            <Card key={trip.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => onSelectTrip(trip)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg line-clamp-2">{trip.title}</CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {trip.destination}, {trip.destination_country}
                    </CardDescription>
                  </div>
                  <Badge variant={trip.is_active ? "default" : "secondary"}>
                    {trip.is_active ? "Active" : "Archived"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {format(new Date(trip.start_date), "MMM d")} - {format(new Date(trip.end_date), "MMM d, yyyy")}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="capitalize">
                    {trip.trip_type}
                  </Badge>
                  
                  {trip.budget && (
                    <div className="flex items-center gap-1 text-sm">
                      <DollarSign className="h-3 w-3" />
                      <span>{trip.budget} {trip.currency}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={(e) => handleEditTrip(trip, e)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="flex-1">
                    <Settings className="h-4 w-4 mr-2" />
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TripEditModal
        trip={editingTrip}
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onTripUpdated={handleTripUpdated}
      />
    </div>
  );
}